import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ActivityIndicator } from '../../../components/common/activity-indicator/activity-indicator';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import WebFlashMessage from '../../../components/common/web-flash-message/web-flash-message';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { VehicleTabs } from '../../../components/sections/vehicle-portal/components/vehicle-tabs/vehicle-tabs';
import { getVinFromLocalStorageIfExists } from '../../../components/sections/vehicle-selector-fds/vin-selector/vin-selector';
import ServerContext from '../../../contexts/serverContext';
import { useAnalytics } from '../../../hooks/use-analytics';
import useSeoConfig from '../../../hooks/use-seo-config';
import { VehicleAttributes } from '../../../models/vehicle-attributes';
import { useFmaLoginValueContent } from '../../components/FMALogin/hook/use-fma-login';
import SupportFdsWrapper from '../../components/support-fds-wrapper/support-fds-wrapper';
import siteMapService from '../../services/site-map-service/site-map-service';
import {
    OwnerManualProps,
    useOwnerManualContent,
} from '../owner-manual/hook/use-owner-manual-content';
import { useOwnerManualData } from '../owner-manual/hook/use-owner-manual-data';
import './owner-manual-details.scss';
import ProfileService from '../../../services/profile-service/profile-service';
import useUserProfile from '../../../hooks/use-user-profile';
import { useVehicleSelectorTabsContent } from '../../../components/common/vehicle-selector-tabs/hooks/use-vehicle-selector-tabs-content';
import VehicleSelectorTabs from '../../../components/common/vehicle-selector-tabs/vehicle-selector-tabs';
import { CatalanLanguageToggle } from '../../../components/sections/catalan-language-switch-toggle/catalan-language-toggle';
import { OwnerManualCategories } from '../owner-manual/hook/owner-manual-categories';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';
import { useHelpCardsContent } from '../../../views/page-not-found-view/hooks/use-help-cards-content';
import OwnerManualError from '../../components/owner-manual-error/owner-manual-error';
import OwnerManualBreadCrumbs from '../../components/owner-manual-bread-crumbs/owner-manual-bread-crumbs';
import { scrollToTop } from '../../../components/sections/vehicle-health/vha-utils';
import { useOwnerManualsAiMappingContent } from '../owner-manual/hook/use-owner-manuals-ai-mapping-content';
import { PageNotFoundView } from '../../../views';
import useFooterDisplay from '../../../hooks/use-footer-display';
import {
    displayEUFooter,
    hideEUFooter,
} from '../../../components/utils/syndicate-util/syndicate-util';
import { EU_COUNTRIES } from '../../../constants';

interface OwnerManualLoadingProps {
    ownerManualContent: OwnerManualProps | null;
    loading: boolean;
}

export const OwnerManualPageLoading = (props: OwnerManualLoadingProps) => {
    return props.ownerManualContent && props.loading ? (
        <>
            <ActivityIndicator />
        </>
    ) : (
        <>
            <PageNotFoundView />
        </>
    );
};

const OwnerManualDetailsPage = () => {
    const serverContext = useContext(ServerContext);
    const fmaLoginValueContent = useFmaLoginValueContent();
    const { model, year } = useParams<{
        model: string;
        year: string;
    }>();
    let ownerManualContent = useOwnerManualContent();
    const [loading, setLoading] = useState(true);
    const [manualsLoading, setManualsLoading] = useState(true);

    const [vin, setVin] = useState('');
    const history = useHistory();
    const location = useLocation<any>();
    const vehicleSelectorTabsContent = useVehicleSelectorTabsContent();
    const searchContent = useSearchContent();
    const helpCardsContent = useHelpCardsContent();
    const profileService = new ProfileService();
    const profile = useUserProfile();
    const [toggleSwitch, setToggleSwitch] = useState<boolean>(false);
    const countryCode = serverContext.current3LetterCountryCode;
    const isSpain = countryCode === 'esp';
    const [catalanToggle, setCatalanToggle] = useState<boolean>(false);
    const [isAiSearch, setIsAiSearch] = useState<boolean>(false);
    const [defaultSpanishToggle, setDefaultSpanishToggle] = useState<boolean>(
        false
    );

    let timeOut: any = null;
    const startTimeout = () => {
        timeOut = setTimeout(() => {
            if (loading) {
                ownerManualContent = null;
                setLoading(false);
            }
        }, 2000);
    };
    const currentRoot = serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';

    const [errorMsg, setErrorMsg] = useState('');
    const ownerManualsAiMappingContent = useOwnerManualsAiMappingContent();
    const [fireEvents] = useAnalytics();

    const downloadAnalytic = (guideName: string) => {
        fireEvents(
            'owner-manuals-guide-download',
            undefined,
            {
                guideName: guideName,
            },
            false
        );
    };

    const ownerManualData = useOwnerManualData(
        setManualsLoading,
        year,
        model,
        vin
    );

    const ownerManualCaData = useOwnerManualData(
        setManualsLoading,
        year,
        model,
        vin,
        'CA-ES'
    );

    useSeoConfig('owner-manuals-details', 'owner-manuals', [
        {
            fieldName: 'year',
            fieldValue: ownerManualData?.metadata.modelYear || '',
        },
        {
            fieldName: 'model',
            fieldValue: ownerManualData?.metadata.displayModelName || '',
        },
    ]);

    useEffect(() => {
        startTimeout();
        return () => {
            clearTimeout(timeOut);
        };
    });
    useEffect(() => {
        if (ownerManualData) {
            setManualsLoading(false);
            scrollToTop();
        }
    }, []);
    const checkForAiSearchableVehicle = (
        model: string,
        year: string,
        seoName: string
    ) => {
        let vehicleMatch = false;

        if (ownerManualsAiMappingContent?.enUsModels)
            vehicleMatch = ownerManualsAiMappingContent?.enUsModels?.some(
                vehicle => {
                    return (
                        vehicle?.model?.toLowerCase() ===
                            model?.toLowerCase() &&
                        vehicle?.years?.split(',').includes(year)
                    );
                }
            );

        if (vehicleMatch) {
            localStorage.setItem('AI_VEHICLE_YEAR', year);
            localStorage.setItem('AI_VEHICLE_MODEL', model);
            seoName && localStorage.setItem('AI_VEHICLE_MODEL_SEO', seoName);
        }
        return vehicleMatch || false;
    };
    useEffect(() => {
        setErrorMsg('');
        if (
            ownerManualContent &&
            !ownerManualContent.hideContent &&
            ownerManualContent.title != 'loading' &&
            ownerManualData
        ) {
            if (ownerManualData?.metadata.error) {
                setErrorMsg(ownerManualContent.detailsEmptyApiResponse);
            }
        }
    }, [ownerManualContent?.hideContent, ownerManualData]);
    useEffect(() => {
        ownerManualsAiMappingContent &&
            setIsAiSearch(
                checkForAiSearchableVehicle(
                    ownerManualData?.metadata?.displayModelName || '',
                    ownerManualData?.metadata?.modelYear || '',
                    ownerManualData?.metadata?.seoName || ''
                )
            );
    }, [ownerManualData, ownerManualsAiMappingContent]);

    useEffect(() => {
        const vinIfExists = getVinFromLocalStorageIfExists();
        if (!vinIfExists && !year && !model) {
            history.push(
                siteMapService.getPathFromRouterConfigs(
                    serverContext.currentLanguageRegionCode,
                    serverContext.brand,
                    'OwnerManualPage',
                    []
                )
            );
        }

        setVin(vinIfExists);
    }, []);
    useEffect(() => {
        const vinIfExists = getVinFromLocalStorageIfExists();

        if (vinIfExists && profile?.profile?.email) {
            profileService.persistVehicleSelection(
                profile.profile.email,
                vinIfExists
            );
        }
    }, [profile]);

    useEffect(() => {
        // Anchor to warranty guide section while coming from warranty page
        if (location && location.state?.scrollTop) {
            const warrantyGuideSection = document.getElementById(
                'Warranty-Guide-h2id'
            );
            if (warrantyGuideSection) {
                warrantyGuideSection.scrollIntoView(true);
            }
        }
    }, [location]);

    const handleVehicleChange = (
        _title: string,
        _selectedIndex: number,
        vehicleAttributes: VehicleAttributes
    ) => {
        if (vehicleAttributes.vin !== vin) {
            const vehicleChangePath: any = siteMapService.getPathFromRouterConfigs(
                serverContext.currentLanguageRegionCode,
                serverContext.brand,
                'OwnerManualWithVin',
                [vehicleAttributes.vin || '']
            );
            history.push(vehicleChangePath);
            localStorage.setItem('USER_VIN', vehicleAttributes.vin || '');
            sessionStorage.setItem('QUERY_VIN', vehicleAttributes.vin || '');
            setVin(vehicleAttributes.vin || '');
        }
    };
    const handleVinChange = (newVin: string): void => {
        setVin(newVin);
    };
    const handleYmmChange = (): void => {
        setVin('');
    };
    const toLandingPageButtonClick = () => {
        fireEvents(
            'owner-manuals-guide-cta',
            undefined,
            {
                ctaType: 'search for another vehicle',
            },
            false
        );
        sessionStorage.removeItem('QUERY_VIN');
        localStorage.removeItem('USER_VIN');
    };
    useFooterDisplay(
        serverContext.brand === 'ford'
            ? [ownerManualContent]
            : [
                  ownerManualContent,
                  fmaLoginValueContent,
                  helpCardsContent,
                  searchContent,
              ]
    );

    useEffect(() => {
        if (manualsLoading || loading) {
            hideEUFooter();
        }
    }, [manualsLoading, loading]);

    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode) &&
            ownerManualContent &&
            fmaLoginValueContent &&
            helpCardsContent &&
            searchContent
        ) {
            displayEUFooter();
        }
    }, [
        ownerManualContent,
        fmaLoginValueContent,
        helpCardsContent,
        searchContent,
    ]);

    return (
        <>
            {manualsLoading ? (
                <ActivityIndicator className="owner-manuals-loading" />
            ) : (
                <>
                    {ownerManualContent &&
                    ownerManualContent.title != 'loading' ? (
                        <SupportFdsWrapper>
                            <WebFlashMessage
                                experience={'owner-manual'}
                                fragmentName={'experience-notification-banner'}
                            />
                            <OwnerManualBreadCrumbs
                                currentRoot={currentRoot}
                                ownerManualContent={ownerManualContent}
                                ownerManualData={ownerManualData}
                                vin={vin}
                                isAiSearch={isAiSearch}
                            />

                            {ownerManualContent && (
                                <div className="owner-manual-content">
                                    <OwnerManualError
                                        errorMsg={errorMsg}
                                        ownerManualData={ownerManualData}
                                        ownerManualContent={ownerManualContent}
                                        toggleSwitch={toggleSwitch}
                                    />

                                    {!vehicleSelectorTabsContent?.vehicleSelectorTabsTitle && (
                                        <div className="vehicle-actions">
                                            <div className="owner-manual-details__left-vehicle-actions">
                                                <div className="owner-manual-button-container">
                                                    <span className="to-owner-label">
                                                        <a
                                                            href={`${currentRoot}${siteMapService.getPathFromRouterConfigs(
                                                                serverContext.currentLanguageRegionCode,
                                                                serverContext.brand,
                                                                'OwnerManualPage',
                                                                []
                                                            )}`}
                                                            onClick={
                                                                toLandingPageButtonClick
                                                            }
                                                        >
                                                            {
                                                                ownerManualContent.detailsSelectNewVehicle
                                                            }
                                                        </a>
                                                    </span>
                                                    <FdsChevron
                                                        className="to-owner-manual"
                                                        direction="left"
                                                        type="unfilled"
                                                    />
                                                </div>
                                                <VehicleTabs
                                                    page="VIN"
                                                    onChange={
                                                        handleVehicleChange
                                                    }
                                                    dropDownOnly={true}
                                                    dropDownPlaceholder={
                                                        fmaLoginValueContent?.noVehiclesMessage
                                                    }
                                                />
                                            </div>
                                            {isSpain && (
                                                <CatalanLanguageToggle
                                                    setToggleSwitch={
                                                        setToggleSwitch
                                                    }
                                                    ownerManualData={
                                                        ownerManualData
                                                    }
                                                    ownerManualContent={
                                                        ownerManualContent
                                                    }
                                                    ownerManualCaData={
                                                        ownerManualCaData
                                                    }
                                                    setErrorMsg={setErrorMsg}
                                                    catalanToggle={
                                                        catalanToggle
                                                    }
                                                    setCatalanToggle={
                                                        setCatalanToggle
                                                    }
                                                    defaultSpanishToggle={
                                                        defaultSpanishToggle
                                                    }
                                                    setDefaultSpanishToggle={
                                                        setDefaultSpanishToggle
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}

                                    {!ownerManualData?.metadata.error &&
                                        !toggleSwitch && (
                                            <OwnerManualCategories
                                                ownerManualContent={
                                                    ownerManualContent
                                                }
                                                ownerManualData={
                                                    ownerManualData
                                                }
                                                downloadAnalytic={
                                                    downloadAnalytic
                                                }
                                            />
                                        )}
                                    {isSpain &&
                                        !ownerManualCaData?.metadata.error &&
                                        toggleSwitch && (
                                            <OwnerManualCategories
                                                ownerManualContent={
                                                    ownerManualContent
                                                }
                                                ownerManualData={
                                                    ownerManualCaData
                                                }
                                                downloadAnalytic={
                                                    downloadAnalytic
                                                }
                                            />
                                        )}
                                    {vehicleSelectorTabsContent?.vehicleSelectorTabsTitle && (
                                        <VehicleSelectorTabs
                                            redirectData={{
                                                path: siteMapService.getPathFromRouterConfigs(
                                                    serverContext.currentLanguageRegionCode,
                                                    serverContext.brand,
                                                    'OwnerManualDetailsPage',
                                                    []
                                                ),
                                                id: 'owner-manual-details',
                                            }}
                                            content={vehicleSelectorTabsContent}
                                            handleVinChange={handleVinChange}
                                            ymmSubmitEvent={handleYmmChange}
                                        />
                                    )}
                                    {ownerManualContent.relatedTopicsContent && (
                                        <>
                                            <h2 className="info-title">
                                                {
                                                    ownerManualContent.relatedTopicsTitle
                                                }
                                            </h2>
                                            <div
                                                className="owner-manual-content-3"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        ownerManualContent.relatedTopicsContent,
                                                }}
                                            />
                                        </>
                                    )}
                                    <DisclosuresLinkSyndicationAware />
                                </div>
                            )}
                        </SupportFdsWrapper>
                    ) : (
                        <OwnerManualPageLoading
                            ownerManualContent={ownerManualContent}
                            loading={loading}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default OwnerManualDetailsPage;
